import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import CONFIG from './config';

export function getImgUrl(path) {
    if (!path) return '';
    if (path.startsWith('http://') || path.startsWith('https://')) {
        return path;
    }
    if (path.startsWith('ConfigArchivos') || path.startsWith('uploads/')) {
        return `${CONFIG.API_URL}/${path}`;
    }
    if (path.startsWith('img/')) {
        return `${CONFIG.BASE_URL}${path}`;
    }
    return `${CONFIG.API_URL}/${path}`;
}

export function show_alerta(mensaje, icono, foco = '') {
    onfocus(foco);
    const MySwal = withReactContent(Swal);

    // Si es un error crítico capaz queremos que la lean bien, le damos más tiempo.
    const tiempo = icono === 'error' ? 5000 : 3000;

    MySwal.fire({
        toast: true,
        position: 'top-end',
        title: mensaje,
        icon: icono,
        showConfirmButton: false,
        timer: tiempo,
        timerProgressBar: true,
        customClass: {
            popup: 'rounded-4 shadow-sm border',
            title: 'fs-6 fw-normal'
        }
    });
}

function onfocus(foco) {
    if (foco !== '') {
        document.getElementById(foco).focus();
    }
}

/**
 * Obtiene las opciones de un tipo de calificación valorativa (Valorativa, Valorativa 1, Valorativa 2, Valorativa 3).
 * Siempre asegura que el índice 0 sea "" y las opciones sigan en orden desde el índice 1 (la menor).
 */
export function obtenerOpcionesValorativa(tipo, configuracion) {
    const isValorativa3 = (tipo === 'Valorativa 3');
    const isValorativa2 = (tipo === 'Valorativa 2');
    const rawVal = isValorativa3 
        ? configuracion?.formato_valoracion3 
        : (isValorativa2 ? configuracion?.formato_valoracion2 : configuracion?.formato_valoracion);
    const defaultOp = isValorativa3
        ? ['', '6 (Regular)', '7 (Bueno)', '8 (BUENO)', '8 (Muy bueno)', '9 (Muy bueno)', '10 (Sobresaliente)']
        : (isValorativa2 
            ? ['', 'TED', 'TEP', 'TEA'] 
            : ['', 'Regular', 'Bueno', 'Muy bueno', 'Sobresaliente']);
        
    if (rawVal && typeof rawVal === 'string' && rawVal.trim() !== '') {
        try {
            const jsonStr = rawVal.trim().replace(/'/g, '"');
            const parsed = JSON.parse(jsonStr);
            if (Array.isArray(parsed) && parsed.length > 0) {
                let list = [...parsed];
                if (list[0] !== '') list.unshift('');
                return list;
            }
        } catch (e) {
            let splitted = rawVal.trim().split(',').map(s => s.trim().replace(/^['"]|['"]$/g, ''));
            if (splitted.length > 0) {
                if (splitted[0] !== '') splitted.unshift('');
                return splitted;
            }
        }
    }
    return defaultOp;
}

/**
 * Determina si una calificación es la menor o reprobada (para mostrar en color rojo).
 * Para numéricas: < 6.
 * Para valorativas: si el valor coincide con el índice 1 (siendo el índice 0 = "").
 */
export function esCalificacionBaja(valor, tipoCalificacion, configuracion) {
    if (valor === null || valor === undefined || String(valor).trim() === '') return false;
    const valStr = String(valor).trim();
    
    // Si es una nota numérica
    const num = parseFloat(valStr);
    if (!isNaN(num) && /^-?\d+(\.\d+)?$/.test(valStr)) {
        return num < 6;
    }
    
    // Si es valorativa (Valorativa, Valorativa 1, Valorativa 2, Valorativa 3)
    const opcionesVal1 = obtenerOpcionesValorativa('Valorativa 1', configuracion);
    const opcionesVal2 = obtenerOpcionesValorativa('Valorativa 2', configuracion);
    const opcionesVal3 = obtenerOpcionesValorativa('Valorativa 3', configuracion);

    if (tipoCalificacion === 'Valorativa 3') {
        const menor = opcionesVal3[1];
        return !!(menor && valStr.toLowerCase() === String(menor).trim().toLowerCase());
    } else if (tipoCalificacion === 'Valorativa 2') {
        const menor = opcionesVal2[1];
        return !!(menor && valStr.toLowerCase() === String(menor).trim().toLowerCase());
    } else if (tipoCalificacion === 'Valorativa' || tipoCalificacion === 'Valorativa 1') {
        const menor = opcionesVal1[1];
        return !!(menor && valStr.toLowerCase() === String(menor).trim().toLowerCase());
    }

    // Si no viene tipoCalificacion especificado, probar si coincide con el índice 1 de alguno de los formatos
    const menor1 = opcionesVal1[1];
    const menor2 = opcionesVal2[1];
    const menor3 = opcionesVal3[1];
    if ((menor1 && valStr.toLowerCase() === String(menor1).trim().toLowerCase()) ||
        (menor2 && valStr.toLowerCase() === String(menor2).trim().toLowerCase()) ||
        (menor3 && valStr.toLowerCase() === String(menor3).trim().toLowerCase())) {
        return true;
    }

    return false;
}

/**
 * Determina si una calificación valorativa coincide con el índice 2 de la escala (para mostrar en amarillo).
 */
export function esCalificacionIndice2(valor, tipoCalificacion, configuracion) {
    if (valor === null || valor === undefined || String(valor).trim() === '') return false;
    const valStr = String(valor).trim();

    // Si es valorativa (Valorativa, Valorativa 1, Valorativa 2, Valorativa 3)
    const opcionesVal1 = obtenerOpcionesValorativa('Valorativa 1', configuracion);
    const opcionesVal2 = obtenerOpcionesValorativa('Valorativa 2', configuracion);
    const opcionesVal3 = obtenerOpcionesValorativa('Valorativa 3', configuracion);

    if (tipoCalificacion === 'Valorativa 3') {
        const item2 = opcionesVal3[2];
        return !!(item2 && valStr.toLowerCase() === String(item2).trim().toLowerCase());
    } else if (tipoCalificacion === 'Valorativa 2') {
        const item2 = opcionesVal2[2];
        return !!(item2 && valStr.toLowerCase() === String(item2).trim().toLowerCase());
    } else if (tipoCalificacion === 'Valorativa' || tipoCalificacion === 'Valorativa 1') {
        const item2 = opcionesVal1[2];
        return !!(item2 && valStr.toLowerCase() === String(item2).trim().toLowerCase());
    }

    // Fallback si no viene tipoCalificacion especificado, probar con el índice 2 de los formatos
    const item2_1 = opcionesVal1[2];
    const item2_2 = opcionesVal2[2];
    const item2_3 = opcionesVal3[2];
    if ((item2_1 && valStr.toLowerCase() === String(item2_1).trim().toLowerCase()) ||
        (item2_2 && valStr.toLowerCase() === String(item2_2).trim().toLowerCase()) ||
        (item2_3 && valStr.toLowerCase() === String(item2_3).trim().toLowerCase())) {
        return true;
    }

    return false;
}

export const esCalificacionIndice3 = esCalificacionIndice2;

/**
 * Calcula el estado actual de una lista de instancias de calificación:
 * - Si hay alguna abierta actualmente, devuelve cuáles son y hasta cuándo están abiertas.
 * - Si están todas cerradas, devuelve cuál fue la última en cerrar (y cuándo cerró)
 *   y cuál es la próxima en abrir (y cuándo abre).
 */
export function obtenerEstadoInstancias(instancias) {
    if (!Array.isArray(instancias) || instancias.length === 0) return null;

    const ahora = new Date();
    const parseFecha = (str) => {
        if (!str) return null;
        const d = new Date(String(str).replace(' ', 'T'));
        return isNaN(d.getTime()) ? null : d;
    };

    const formatearFecha = (d) => {
        if (!d) return '';
        const dia = String(d.getDate()).padStart(2, '0');
        const mes = String(d.getMonth() + 1).padStart(2, '0');
        const anio = d.getFullYear();
        const horas = String(d.getHours()).padStart(2, '0');
        const min = String(d.getMinutes()).padStart(2, '0');
        return `${dia}/${mes}/${anio} (${horas}:${min} hs)`;
    };

    const normalizadas = instancias
        .map(inst => ({
            id: inst.id,
            nombre: inst.nombre_instancia || inst.nombre || '',
            inicio: parseFecha(inst.fecha_inicio || inst.fechaDesde),
            cierre: parseFecha(inst.fecha_cierre || inst.fechaHasta)
        }))
        .filter(inst => inst.nombre && inst.inicio && inst.cierre);

    if (normalizadas.length === 0) return null;

    const abiertas = normalizadas.filter(inst => inst.inicio <= ahora && ahora <= inst.cierre);

    if (abiertas.length > 0) {
        return {
            abierta: true,
            abiertas: abiertas.map(inst => ({
                ...inst,
                cierreFormateado: formatearFecha(inst.cierre)
            }))
        };
    }

    const cerradas = normalizadas
        .filter(inst => inst.cierre < ahora)
        .sort((a, b) => b.cierre - a.cierre);

    const proximas = normalizadas
        .filter(inst => inst.inicio > ahora)
        .sort((a, b) => a.inicio - b.inicio);

    const ultimaCerrada = cerradas.length > 0 ? {
        ...cerradas[0],
        cierreFormateado: formatearFecha(cerradas[0].cierre)
    } : null;

    const proximaAbrir = proximas.length > 0 ? {
        ...proximas[0],
        inicioFormateado: formatearFecha(proximas[0].inicio)
    } : null;

    return {
        abierta: false,
        ultimaCerrada,
        proximaAbrir
    };
}


